import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Receipt, ArrowRight, Printer } from "lucide-react";
import { PageHeader } from "@/components/ui/PageHeader";
import { Tabs } from "@/components/ui/Tabs";
import { supabase } from "@/lib/supabase";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { InfoTip } from "@/components/InfoTip";
import { displayCustomerName } from "@/lib/format";
import { useInvoiceSendStatuses, InvoiceSentChip } from "@/features/comms/invoiceSend";
import { useRealtimeRefresh } from "@/lib/useRealtimeRefresh";

// ============================================================================
// INV-LIST-UNIFY — the one place all invoices live (Seth 2026-08-11: created a
// Pegasus invoice, went to Reports → Invoice List, couldn't find it — that list
// reads the ESC mirror only). This view unifies BOTH sources, source-tagged:
//   - Pegasus-native invoices (pegasus_job_invoice) — drafts, issued, the works
//   - ESC invoices (mirror_sales) — the historical + office-entered book
// First-class side-menu home (ServiceTitan/Jobber pattern); Reports keeps the
// analytical engine view.
// INVOICE-SENT-01 (2026-09-17): a Sent column read from the outbox (the tech's
// send on Complete and the office's from the invoice screen land in the same
// place) + a "Not sent" filter — billable Pegasus invoices nobody has emailed,
// with Email it right on the row. /invoices?filter=unsent opens on that view.
// ============================================================================

interface PegRow {
  id: string;
  status: string | null;
  total: number | null;
  custno: string | null;
  technician: string | null;
  type: string | null;
  created_at: string;
  completed_date: string | null;
}
interface EscRow {
  invoice: string;
  custno: string | null;
  inv_date: string | null;
  inv_amount: number | null;
  dept: string | null;
  salesman: string | null;
}
interface Unified {
  key: string;
  source: "pegasus" | "esc";
  display: string;
  date: string | null;
  custno: string | null;
  detail: string | null; // tech or dept
  total: number | null;
  status: string; // draft | issued | esc
  escNo?: string | null; // pegasus invoices that have POSTED to ESC
  nav: string;
}

const money = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : n.toLocaleString("en-US", { style: "currency", currency: "USD" });

/** The short reference Pegasus shows for a native invoice (SH-8A6A9E style). */
const pegRef = (id: string) => "SH-" + id.slice(0, 6).toUpperCase();

function usePegasusInvoices(q: string) {
  return useQuery({
    queryKey: ["invoices_unified_peg", q],
    queryFn: async (): Promise<PegRow[]> => {
      let sel = supabase
        .from("pegasus_job_invoice")
        .select("id, status, total, custno, technician, type, created_at, completed_date")
        .order("created_at", { ascending: false })
        .limit(120);
      const n = q.trim();
      if (n) sel = sel.or(`custno.ilike.%${n}%,technician.ilike.%${n}%,type.ilike.%${n}%`);
      const { data, error } = await sel;
      if (error) throw error;
      return (data ?? []) as PegRow[];
    },
  });
}

function useEscInvoices(q: string) {
  return useQuery({
    queryKey: ["invoices_unified_esc", q],
    queryFn: async (): Promise<EscRow[]> => {
      let sel = supabase
        .from("mirror_sales")
        .select("invoice, custno, inv_date, inv_amount, dept, salesman")
        .order("inv_date", { ascending: false })
        .limit(120);
      const n = q.trim();
      if (n) {
        // Digit searches match the zero-padded ESC number naturally via ilike.
        sel = sel.or(`invoice.ilike.%${n}%,custno.ilike.%${n}%`);
      }
      const { data, error } = await sel;
      if (error) throw error;
      return (data ?? []) as EscRow[];
    },
  });
}

/** Names for the visible custnos, one batched lookup. */
function useCustomerNames(custnos: string[]) {
  const key = custnos.slice().sort().join(",");
  return useQuery({
    queryKey: ["invoices_unified_names", key],
    enabled: custnos.length > 0,
    queryFn: async (): Promise<Record<string, string>> => {
      const { data } = await supabase.from("mirror_customer").select("custno, fullname").in("custno", custnos.slice(0, 200));
      const out: Record<string, string> = {};
      for (const r of (data ?? []) as { custno: string; fullname: string | null }[]) {
        if (r.fullname) out[r.custno] = displayCustomerName(null, r.fullname);
      }
      return out;
    },
  });
}

// ============================================================================
// PAPER-INVOICE-01 — the To-Mail queue. A finalized invoice for a customer
// with the "Paper invoices" preference lands here automatically (DB trigger);
// the office prints it, drops it in the mail, and hits Mark mailed. The queue
// IS the prompt — nobody has to remember a note. Self-hides when empty.
// ============================================================================
interface PaperMailRow {
  id: string;
  invoice_id: string;
  custno: string | null;
  created_at: string;
  invoice: { invoice_no: string | null; total: number | null; completed_date: string | null; type: string | null; job_name: string | null } | null;
}
function usePaperMailQueue() {
  return useQuery({
    queryKey: ["paper_mail_pending"],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<PaperMailRow[]> => {
      const { data, error } = await supabase
        .from("pegasus_paper_mail")
        .select("id, invoice_id, custno, created_at, invoice:pegasus_job_invoice(invoice_no, total, completed_date, type, job_name)")
        .eq("status", "pending")
        .order("created_at");
      if (error) throw error;
      return (data ?? []) as unknown as PaperMailRow[];
    },
  });
}
function ToMailQueue() {
  const qc = useQueryClient();
  const { data: rows } = usePaperMailQueue();
  const names = useCustomerNames(
    useMemo(() => Array.from(new Set((rows ?? []).map((r) => r.custno).filter(Boolean))) as string[], [rows]),
  ).data ?? {};
  const mark = useMutation({
    mutationFn: async (id: string) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("pegasus_paper_mail")
        .update({ status: "mailed", mailed_at: new Date().toISOString(), mailed_by: u?.user?.email ?? null, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["paper_mail_pending"] }),
  });
  if (!rows || rows.length === 0) return null;
  return (
    <Card className="mb-4">
      <div className="flex items-center gap-2.5 border-b border-hairline/70 px-4 py-2.5">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
          <Printer className="h-4 w-4" />
        </span>
        <span className="text-sm font-semibold text-slate-900">To Mail — paper invoices</span>
        <Badge>{rows.length}</Badge>
        <span className="ml-auto text-[11px] text-slate-400">These customers get printed invoices by mail — print, send, mark it done.</span>
      </div>
      <ul className="divide-y divide-hairline">
        {rows.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5">
            <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">
              {names[r.custno ?? ""] ?? r.custno ?? "—"}
              <span className="ml-2 font-normal text-slate-400">#{r.custno}</span>
            </span>
            <span className="text-sm tabular-nums text-slate-600">
              {r.invoice?.invoice_no ? `Inv ${r.invoice.invoice_no}` : (r.invoice?.type ?? "invoice")}
              {r.invoice?.total != null && ` · $${Number(r.invoice.total).toLocaleString("en-US", { maximumFractionDigits: 2 })}`}
              {r.invoice?.completed_date && ` · ${r.invoice.completed_date}`}
            </span>
            <button
              type="button"
              disabled={mark.isPending}
              onClick={() => mark.mutate(r.id)}
              className="rounded-full bg-brand-700 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-brand-800 disabled:opacity-60"
              title="Records who mailed it and when"
            >
              Mark mailed
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Pegasus invoices already posted into ESC (the bridge ledger). */
function useEscPostMap() {
  return useQuery({
    queryKey: ["invoices_esc_post_map"],
    queryFn: async (): Promise<Record<string, string>> => {
      const { data } = await supabase.from("pegasus_esc_post").select("invoice_id, esc_invoice_no");
      const out: Record<string, string> = {};
      for (const r of (data ?? []) as { invoice_id: string; esc_invoice_no: string }[]) out[r.invoice_id] = r.esc_invoice_no;
      return out;
    },
  });
}

function useDeptNames() {
  return useQuery({
    queryKey: ["invoices_dept_names"],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<Record<string, string>> => {
      const { data } = await supabase.from("mirror_department").select("dept_code, dept_name");
      const out: Record<string, string> = {};
      for (const r of (data ?? []) as { dept_code: string; dept_name: string | null }[]) out[r.dept_code] = r.dept_name ?? r.dept_code;
      return out;
    },
  });
}

export function InvoicesView() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [source, setSource] = useState<"all" | "pegasus" | "esc" | "unsent">(searchParams.get("filter") === "unsent" ? "unsent" : "all");

  const peg = usePegasusInvoices(q);
  // INVOICE-LIVE-01: a tech's invoice appears (and changes) here as they work.
  useRealtimeRefresh(["pegasus_job_invoice"], [["invoices_unified_peg"]]);
  const esc = useEscInvoices(q);
  const postMap = useEscPostMap().data ?? {};
  const depts = useDeptNames().data ?? {};

  const rows = useMemo<Unified[]>(() => {
    const out: Unified[] = [];
    for (const p of peg.data ?? []) {
      out.push({
        key: "p:" + p.id,
        source: "pegasus",
        display: pegRef(p.id),
        date: p.completed_date ?? p.created_at.slice(0, 10),
        custno: p.custno,
        detail: p.technician ?? (p.type ? p.type[0].toUpperCase() + p.type.slice(1) : null),
        total: p.total,
        status: p.status ?? "draft",
        escNo: postMap[p.id] ?? null,
        nav: `/invoices/${p.id}`,
      });
    }
    for (const e of esc.data ?? []) {
      out.push({
        key: "e:" + e.invoice,
        source: "esc",
        display: e.invoice.replace(/^0+/, "") || e.invoice,
        date: e.inv_date ? e.inv_date.slice(0, 10) : null,
        custno: e.custno,
        detail: e.dept ? depts[e.dept] ?? `Dept ${e.dept}` : null,
        total: e.inv_amount,
        status: "esc",
        nav: `/invoices/esc/${e.invoice}`,
      });
    }
    out.sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));
    return out.filter((r) => source === "all" || source === "unsent" || r.source === source);
  }, [peg.data, esc.data, source, postMap, depts]);

  // The send state of every row in view: Pegasus invoices by id, ESC invoices
  // by `esc:<no>` (only an email sent FROM Pegasus is knowable for those).
  const sendRefs = useMemo(() => rows.map((r) => (r.source === "pegasus" ? r.key.slice(2) : `esc:${r.key.slice(2)}`)), [rows]);
  const sends = useInvoiceSendStatuses(sendRefs).data;
  const sendOf = (r: Unified) => sends?.get(r.source === "pegasus" ? r.key.slice(2) : `esc:${r.key.slice(2)}`);
  const isUnsent = (r: Unified) => {
    // Issued (the truck's service invoices) and finalized (an install's final invoice, from close).
    if (r.source !== "pegasus" || (r.status !== "issued" && r.status !== "finalized") || !((r.total ?? 0) > 0)) return false;
    const st = sendOf(r)?.state ?? "none";
    return st === "none" || st === "failed" || st === "suppressed";
  };
  const shown = useMemo(() => (source === "unsent" ? rows.filter(isUnsent) : rows), [rows, source, sends]); // eslint-disable-line react-hooks/exhaustive-deps
  const unsentCount = useMemo(() => rows.filter(isUnsent).length, [rows, sends]); // eslint-disable-line react-hooks/exhaustive-deps

  const names = useCustomerNames(useMemo(() => Array.from(new Set(rows.map((r) => r.custno).filter(Boolean))) as string[], [rows])).data ?? {};
  const loading = peg.isLoading || esc.isLoading;

  return (
    <div className="mx-auto max-w-7xl px-6 py-6">
      <PageHeader
        icon={Receipt}
        title="Invoices"
        description="Every invoice in one place — created in Pegasus or living in ESC."
        /* NAV-360-01 spread #1: the same segmented control the 360 wears —
           counts answer the question before the click. */
        actions={
        <Tabs
          variant="pill"
          size="sm"
          value={source}
          onChange={(k) => setSource(k as typeof source)}
          items={[
            { key: "all", label: "All" },
            { key: "pegasus", label: "Pegasus" },
            { key: "esc", label: "ESC" },
            { key: "unsent", label: "Not sent", count: unsentCount > 0 ? unsentCount : null },
          ]}
        />
        }
      />

      {/* PAPER-INVOICE-01: print + mail prompts, right where invoices live. */}
      <ToMailQueue />

      <div className="relative mb-4">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by invoice #, customer #, technician…"
          className="h-11 w-full rounded-xl border border-hairline bg-white pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
        />
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-hairline bg-slate-50/70 text-left text-[12px] font-semibold text-slate-500">
              <th className="px-4 py-2.5">Invoice</th>
              <th className="px-4 py-2.5">Date</th>
              <th className="px-4 py-2.5">Customer</th>
              <th className="px-4 py-2.5">Tech / Dept</th>
              <th className="px-4 py-2.5 text-right">Total</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">
                <span className="inline-flex items-center gap-1.5">
                  Sent to Customer
                  <InfoTip label="Where this comes from">Read from the email outbox. The tech&apos;s send on Complete and the office&apos;s send from the invoice screen both land there.</InfoTip>
                </span>
              </th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {shown.slice(0, 100).map((r) => (
              <tr key={r.key} onClick={() => nav(r.nav)} className="group cursor-pointer border-b border-hairline/60 transition-colors last:border-0 hover:bg-brand-50/40">
                <td className="whitespace-nowrap px-4 py-3">
                  {/* DESIGN-CALM-01: the source tag came off — the number says it
                      (SH-… is Pegasus, a plain number is ESC) and the Status
                      column already says where the record lives. */}
                  <div className="flex items-center gap-2">
                    <span className="font-medium tabular-nums text-slate-900">{r.display}</span>
                    {r.escNo && <Badge size="xs" title="This Pegasus invoice has been posted into ESC">ESC #{r.escNo.replace(/^0+/, "")}</Badge>}
                  </div>
                </td>
                <td className="whitespace-nowrap px-4 py-3 tabular-nums text-slate-600">{r.date ?? "—"}</td>
                <td className="px-4 py-3">
                  <span className="text-slate-900">{(r.custno && names[r.custno]) || "—"}</span>
                  {r.custno && <span className="ml-1.5 text-xs tabular-nums text-slate-400">{r.custno}</span>}
                </td>
                <td className="px-4 py-3 text-slate-600">{r.detail ?? "—"}</td>
                <td className="px-4 py-3 text-right font-medium tabular-nums text-slate-900">{money(r.total)}</td>
                <td className="px-4 py-3">
                  {r.status === "esc" ? (
                    <Badge title="Lives in ESC's books — read-only here">In ESC</Badge>
                  ) : r.status === "issued" || r.status === "finalized" ? (
                    <Badge tone="good">Issued</Badge>
                  ) : r.status === "void" ? (
                    <Badge title="Nothing owed — a deposit's payment moves to the final invoice when the job closes">Void</Badge>
                  ) : (
                    <Badge tone="warn">Draft</Badge>
                  )}
                </td>
                <td className="px-4 py-3">
                  {/* Drafts aren't sendable; an ESC invoice only shows a send made from Pegasus. */}
                  {r.status === "draft" || r.status === "void" ? (
                    <span className="text-xs text-slate-300">—</span>
                  ) : r.source === "esc" && (sendOf(r)?.state ?? "none") === "none" ? (
                    <span className="text-xs text-slate-300" title="ESC invoices go out through ESC or the mail — Pegasus only knows about emails sent from here">—</span>
                  ) : (
                    <InvoiceSentChip
                      status={sendOf(r)}
                      onSend={() => nav(`${r.nav}?email=1`)}
                    />
                  )}
                </td>
                <td className="pr-3"><ArrowRight className="h-4 w-4 text-slate-300 opacity-0 transition-opacity group-hover:opacity-100" /></td>
              </tr>
            ))}
            {!loading && shown.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-slate-500">{source === "unsent" ? "Nothing waiting — every billable invoice in view has a send on record." : `No invoices match${q ? ` "${q}"` : ""}.`}</td></tr>
            )}
            {loading && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-slate-400">Loading invoices…</td></tr>
            )}
          </tbody>
        </table>
      </Card>
      <p className="mt-2 text-xs text-slate-400">Showing the most recent {Math.min(shown.length, 100)} — search to reach anything older.</p>
    </div>
  );
}
